import React, { useRef, useCallback, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import AudioControlButton from '../components/AudioControlButton';
import { Message } from '../types';
import { Audio } from 'expo-av';
import { useChatContext } from '../contexts/ChatContext';
import { useTutor } from '../contexts/TutorContext';
import { getCorrection } from '../api/correction';

interface UserMessageProps {
  message: Message;
}

export const UserMessage: React.FC<UserMessageProps> = ({
  message,
}) => {
  const { userPlayingId, setUserPlayingId } = useChatContext();
  const { selectedTutor } = useTutor();
  const soundRef = useRef<Audio.Sound | null>(null);
  const [correction, setCorrection] = useState<string | null>(null);
  const [isLoadingCorrection, setIsLoadingCorrection] = useState(false);

  const handlePlaybackStatusUpdate = useCallback(async (status: any) => {
    if (status.isLoaded) {
      if (!status.isPlaying && status.didJustFinish) {
        // Reset states when audio finishes
        setUserPlayingId(null);
        
        // Clean up the sound
        if (soundRef.current) {
          soundRef.current.setOnPlaybackStatusUpdate(null);
          await soundRef.current.unloadAsync();
          soundRef.current = null;
        }
      }
    }
  }, [setUserPlayingId]);

  const handlePlayAudio = async () => {
    try {
      const isPlaying = userPlayingId === message.id;
      if (isPlaying && soundRef.current) {
        // Stop current playback
        setUserPlayingId(null);
        
        // Clean up the sound
        soundRef.current.setOnPlaybackStatusUpdate(null);
        await soundRef.current.unloadAsync();
        soundRef.current = null;
      } else {
        // Stop any previous sound
        if (soundRef.current) {
          soundRef.current.setOnPlaybackStatusUpdate(null);
          await soundRef.current.unloadAsync();
          soundRef.current = null;
        }

        if (!message.audioUrl) {
          console.error('No audio URL available');
          return;
        }

        // Create and load the new sound
        const { sound: newSound } = await Audio.Sound.createAsync(
          { uri: message.audioUrl },
          { shouldPlay: true }
        );
        
        // Set up status monitoring
        newSound.setOnPlaybackStatusUpdate(handlePlaybackStatusUpdate);
        soundRef.current = newSound;
        setUserPlayingId(message.id);
      }
    } catch (error) {
      console.error('Error handling audio playback:', error);
      setUserPlayingId(null);
      if (soundRef.current) {
        try {
          soundRef.current.setOnPlaybackStatusUpdate(null);
          await soundRef.current.unloadAsync();
        } catch (cleanupError) {
          console.error('Error cleaning up sound:', cleanupError);
        }
        soundRef.current = null;
      }
    }
  };

  const handleGetCorrection = async () => {
    if (!selectedTutor || (!message.text && !message.transcript)) return;
    
    try {
      setIsLoadingCorrection(true);
      console.log('Getting correction for:', {
        text: message.text || message.transcript,
        language: selectedTutor.language_code,
        tutorId: selectedTutor.id
      });

      const response = await getCorrection(
        message.text || message.transcript || '',
        selectedTutor.language_code
      );
      
      console.log('Correction received:', response);
      if (response.status === 'success' && response.correction?.corrected_text) {
        setCorrection(response.correction.corrected_text);
      } else {
        setCorrection(null);
      }
    } catch (error) {
      console.error('Error in handleGetCorrection:', error);
      setCorrection(null);
    } finally {
      setIsLoadingCorrection(false);
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.messageWrapper}>
        <View style={styles.messageContainer}>
          <View style={styles.contentContainer}>
            <View style={styles.textContainer}>
              {message.transcript ? (
                <>
                  <Text style={styles.transcriptLabel}>Transcript:</Text>
                  <Text style={styles.messageText}>{message.transcript}</Text>
                </>
              ) : (
                <Text style={styles.messageText}>{message.text}</Text>
              )}
              {correction && (
                <Text style={styles.correctionText}>{correction}</Text>
              )}
            </View>
            <View style={styles.buttonsContainer}>
              {message.audioUrl && (
                <TouchableOpacity 
                  style={[styles.button, userPlayingId === message.id && styles.buttonPressed]}
                  onPress={handlePlayAudio}
                >
                  <MaterialIcons 
                    name={userPlayingId === message.id ? "pause" : "play-arrow"} 
                    size={24} 
                    style={styles.buttonIcon} 
                  />
                </TouchableOpacity>
              )}
              {(message.text || message.transcript) && (
                <TouchableOpacity
                  style={[
                    styles.button,
                    isLoadingCorrection && styles.buttonPressed
                  ]}
                  onPress={handleGetCorrection}
                  disabled={isLoadingCorrection}
                >
                  <MaterialIcons 
                    name="spellcheck" 
                    size={24} 
                    style={styles.buttonIcon} 
                  />
                </TouchableOpacity>
              )}
              {isLoadingCorrection && (
                <View style={styles.processingContainer}>
                  <MaterialIcons 
                    name="hourglass-bottom" 
                    size={20} 
                    style={styles.buttonIcon} 
                  />
                  <Text style={styles.processingText}>Processing...</Text>
                </View>
              )}
            </View>
          </View>
        </View>
        <View style={styles.iconContainer}>
          <MaterialIcons name="person" size={20} color="#4B7FDB" />
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignSelf: 'flex-end',
    maxWidth: '100%',
    marginLeft: 5,
    paddingRight: 0,
  },
  messageWrapper: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    marginRight: 5,
    marginVertical: 4,
    justifyContent: 'flex-end',
  },
  messageContainer: {
    backgroundColor: '#4B7FDB',
    borderRadius: 16,
    padding: 4,
    shadowColor: '#2D6BDB',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
    maxWidth: '85%',
  },
  contentContainer: {
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 4,
    padding: 8,
  },
  textContainer: {
    maxWidth: '100%',
  },
  messageText: {
    color: '#FFFFFF',
    fontSize: 15,
    lineHeight: 22,
  },
  transcriptLabel: {
    fontSize: 12,
    color: '#E6F0FF',
    marginTop: 4,
    fontStyle: 'italic',
  },
  correctionText: {
    fontSize: 12,
    color: '#B8E5FF',
    fontStyle: 'italic',
  },
  buttonsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 4,
    padding: 4,
    borderRadius: 16,
  },
  button: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: 'rgba(255, 255, 255, 0.12)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  buttonPressed: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    transform: [{ scale: 0.95 }],
  },
  buttonIcon: {
    color: '#FFFFFF',
  },
  processingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 16,
    marginTop: 4,
  },
  processingText: {
    color: '#FFFFFF',
    marginLeft: 6,
    fontSize: 13,
  },
  iconContainer: {
    width: 32,
    height: 32,
    borderRadius: 16,

    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 4,
    marginRight: 0,
  },
});
